import { Link } from "react-router-dom";
import { Check, CreditCard, Receipt, ArrowRight, Sparkles, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PLANS, CURRENT_USER } from "@/lib/mockData";

const PLAN_BTN_CLASS = {
    pro: "bg-slate-100 dark:bg-slate-800 text-slate-500",
    business: "bg-indigo-600 hover:bg-indigo-700 text-white",
    starter: "bg-slate-900 hover:bg-slate-800 text-white",
};

const PLAN_BTN_LABEL = {
    pro: "Current Plan",
    business: "Upgrade",
    starter: "Downgrade",
};

const INVOICES = [
    { id: "INV-2026-002", date: "12 Feb 2026", amount: "₹499", status: "Paid" },
    { id: "INV-2026-001", date: "12 Jan 2026", amount: "₹499", status: "Paid" },
    { id: "INV-2025-012", date: "12 Dec 2025", amount: "₹499", status: "Paid" },
    { id: "INV-2025-011", date: "12 Nov 2025", amount: "₹299", status: "Paid" },
];

export default function Billing() {
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                    Billing & Plan
                </h1>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Manage your subscription, payment method, and download invoices.
                </p>
            </div>

            {/* Current plan summary */}
            <div className="rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 p-6 lg:p-7">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <Badge className="bg-indigo-600 text-white hover:bg-indigo-600">
                            Current Plan
                        </Badge>
                        <h2 className="mt-3 text-2xl font-bold text-slate-900 dark:text-slate-50">
                            Pro — ₹499 / month
                        </h2>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                            30 analyses / month • Renews on {CURRENT_USER.renewsOn}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            className="border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                        >
                            Manage Subscription
                        </Button>
                        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-500/25">
                            Upgrade to Business <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Available plans */}
            <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-50 mb-4">
                    Available Plans
                </h3>
                <div className="grid lg:grid-cols-3 gap-4">
                    {PLANS.map((p) => (
                        <div
                            key={p.id}
                            className={`rounded-2xl p-6 border transition-all ${
                                p.id === "pro"
                                    ? "border-indigo-300 dark:border-indigo-800 bg-white dark:bg-slate-900 ring-1 ring-indigo-200 dark:ring-indigo-900/40"
                                    : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                            }`}
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <h4 className="font-bold text-slate-900 dark:text-slate-50">
                                        {p.name}
                                    </h4>
                                    <p className="text-xs text-slate-500">{p.credits}</p>
                                </div>
                                {p.id === "pro" && (
                                    <Badge className="bg-indigo-600 text-white text-[10px]">
                                        Current
                                    </Badge>
                                )}
                            </div>
                            <p className="mt-4 text-2xl font-bold font-mono text-slate-900 dark:text-slate-50">
                                ₹{p.price}
                                <span className="text-sm text-slate-400 font-normal">
                                    /mo
                                </span>
                            </p>
                            <Button
                                disabled={p.id === "pro"}
                                className={`mt-5 w-full ${PLAN_BTN_CLASS[p.id]}`}
                            >
                                {PLAN_BTN_LABEL[p.id]}
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Payment method + invoices */}
            <div className="grid lg:grid-cols-3 gap-6">
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                        Payment Method
                    </h3>
                    <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 p-4 flex items-center gap-3">
                        <div className="flex h-10 w-14 items-center justify-center rounded-md bg-gradient-to-br from-slate-900 to-slate-700 text-white font-bold text-xs">
                            VISA
                        </div>
                        <div>
                            <p className="text-sm font-medium font-mono">
                                •••• •••• •••• 4291
                            </p>
                            <p className="text-xs text-slate-500">Expires 06/28</p>
                        </div>
                    </div>
                    <Button variant="outline" className="mt-4 w-full">
                        <CreditCard className="h-4 w-4 mr-2" /> Update Card
                    </Button>
                </div>

                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 lg:col-span-2">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                            Invoice History
                        </h3>
                        <Receipt className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="mt-4 divide-y divide-slate-100 dark:divide-slate-800">
                        {INVOICES.map((inv) => (
                            <div
                                key={inv.id}
                                className="flex items-center justify-between py-3"
                            >
                                <div>
                                    <p className="text-sm font-mono font-medium text-slate-900 dark:text-slate-50">
                                        {inv.id}
                                    </p>
                                    <p className="text-xs text-slate-500">{inv.date}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-sm font-semibold">
                                        {inv.amount}
                                    </span>
                                    <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 text-xs">
                                        {inv.status}
                                    </Badge>
                                    <button className="h-8 w-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-indigo-600">
                                        <Download className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
